import React, { useState } from 'react';
import PayoutDialogShell, { labelCss, inputCss, errorCss, primaryBtn, secondaryBtn } from './PayoutDialogShell';
import { holdSessionPayoutLine, releaseSessionPayoutLine, cancelSessionPayoutLine } from '../../utils/api';
import { fmtRupees, textValid, actionErrorMessage, isStaleCode } from '../../utils/payoutFormat';

const COPY = {
  hold:    { title: 'Hold this payout',    intro: 'Holding stops automatic release. To pay this line later you must release it manually.', label: 'Reason (3–500 characters)', cta: 'Place hold',    done: 'Hold placed.' },
  release: { title: 'Release this payout', intro: 'Releasing makes this line ready to pay.',                                               label: 'Note (3–500 characters)',   cta: 'Release',       done: 'Payout released.' },
  cancel:  { title: 'Cancel this payout',  intro: "This can't be undone. The healer will not be paid for this session.",                   label: 'Reason (3–500 characters)', cta: 'Cancel payout', done: 'Payout cancelled.' },
};

// Hold / release / cancel one line. Release asks again if the patient's payment is not recorded as received.
const PayoutActionDialog = ({ action, line, healerName, onClose, onDone }) => {
  const [text,         setText]         = useState('');
  const [busy,         setBusy]         = useState(false);
  const [error,        setError]        = useState('');
  const [needOverride, setNeedOverride] = useState(false);
  const copy  = COPY[action];
  const valid = textValid(text, 3, 500);

  const run = async function(override) {
    if (busy || !valid) return;
    setBusy(true);
    setError('');
    const t = text.trim();
    try {
      if (action === 'hold')         await holdSessionPayoutLine(line._id, { note: t });
      else if (action === 'release') await releaseSessionPayoutLine(line._id, override ? { note: t, override: true } : { note: t });
      else                           await cancelSessionPayoutLine(line._id, { reason: t });
      onDone(copy.done, false);
    } catch (err) {
      const code = err.response && err.response.data && err.response.data.code;
      if (action === 'release' && code === 'PAYMENT_NOT_RECEIVED') setNeedOverride(true);
      else if (isStaleCode(code)) onDone(actionErrorMessage(err, ''), true);
      else setError(actionErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PayoutDialogShell title={copy.title} onClose={onClose} busy={busy}>
      <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
        {healerName} · session #{line.sessionNumber} · {fmtRupees(line.amount)}
      </div>
      {needOverride ? (
        <div>
          <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
            The patient's payment for this session is not recorded as received. Release anyway?
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={secondaryBtn} onClick={function() { setNeedOverride(false); }} disabled={busy}>Go back</button>
            <button style={primaryBtn(busy)} onClick={function() { run(true); }} disabled={busy}>Release anyway</button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151' }}>{copy.intro}</p>
          <label style={labelCss} htmlFor="payout-text">{copy.label}</label>
          <textarea id="payout-text" value={text} maxLength={500} rows={3} style={inputCss}
            onChange={function(e) { setText(e.target.value); }} disabled={busy} />
          {error && <div style={errorCss}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={secondaryBtn} onClick={onClose} disabled={busy}>Close</button>
            <button style={primaryBtn(!valid || busy)} onClick={function() { run(false); }} disabled={!valid || busy}>
              {busy ? 'Working…' : copy.cta}
            </button>
          </div>
        </div>
      )}
    </PayoutDialogShell>
  );
};

export default PayoutActionDialog;
